import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import {
  FADED_CELL_CLASSNAME,
  formattedUTCDay,
  isPricingModeRelative,
  isTerminalQuoteStatus,
  LIGHTEN_CELL_CLASSNAME,
  type Column,
  type ColumnDef,
  type ExpiryCountdownParams,
  type PriceParams,
  type QuoteStatusColumnParams,
  type SizeColumnParams,
  type SpreadColumnParams,
} from '@talos/kyoko';
import type { CellClassRules } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { PredictionOutcomeTag } from '../../../components/PredictionMarkets/PredictionOutcomeTag';
import { usePredictionMarketBlotterColumns } from '../predictionMarketBlotter';
import { bidCellClassRules, offerCellClassRules } from './columnClassRules';
import { getQuotesV2ResponsesColumn } from './getQuotesV2ResponsesColumn';
import { isPricingRow, QuoteMarketRow, SweepGroupIDRow, type QuotesBlotterRow } from './types';
import {
  useQuotesV2BlotterActionColumn,
  type UseQuotesV2BlotterActionColumnParams,
} from './useQuotesV2BlotterActionColumn';

const childRowFadedCellClassRules: CellClassRules<QuotesBlotterRow> = {
  [FADED_CELL_CLASSNAME]: params => params.data instanceof QuoteMarketRow,
};

export const lightenCellClassRules: CellClassRules<QuotesBlotterRow> = {
  [LIGHTEN_CELL_CLASSNAME]: params => !(params.data instanceof SweepGroupIDRow),
};

export const useQuotesV2Columns = ({ onOpenQuote, onShowDetails }: UseQuotesV2BlotterActionColumnParams): Column[] => {
  const { enrichRFQBlotterV2WithCareOrders } = useFeatureFlag();
  const predictionColumns = usePredictionMarketBlotterColumns();
  const actionColumn = useQuotesV2BlotterActionColumn({ onOpenQuote, onShowDetails });

  const columns = useMemo(() => {
    const baseColumns = compact([
      {
        field: 'SubmitTime',
        type: 'date',
        title: 'Submit Time',
        initialSort: '-',
        initialWidth: 130,
        cellClassRules: childRowFadedCellClassRules,
      },
      {
        field: 'expiryTime',
        type: 'expiryCountdown',
        title: 'Expiry',
        initialWidth: 130,
        params: {
          isExpired: (data: unknown) => isPricingRow(data) && isTerminalQuoteStatus(data.QuoteStatus),
        } satisfies ExpiryCountdownParams<QuotesBlotterRow>,
      },
      {
        field: 'quote.RFQID',
        type: 'id',
        title: 'RFQID',
        initialWidth: 80,
        initialHide: true,
      },
      getQuotesV2ResponsesColumn(),
      {
        field: 'Symbol',
        type: 'security',
        title: 'Symbol',
        initialWidth: 160,
        params: {
          suffix: ({ data }) =>
            isPricingRow(data) && data.ExpressedOutcome != null ? (
              <PredictionOutcomeTag outcome={data.ExpressedOutcome} />
            ) : undefined,
        },
        cellClassRules: childRowFadedCellClassRules,
      },
      {
        field: 'OrderQty',
        type: 'size',
        title: 'Quantity',
        params: { currencyField: 'Currency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        cellClassRules: childRowFadedCellClassRules,
      },
      {
        field: 'quote.TradedQty',
        type: 'size',
        title: 'Traded Qty',
        params: { currencyField: 'quote.Currency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        initialHide: true,
      },
      {
        field: 'Side',
        id: 'Side',
        title: 'Side',
        type: 'quoteSide',
        cellClassRules: childRowFadedCellClassRules,
      },

      // Bid
      {
        type: 'market',
        field: 'bid.Market',
        title: 'Bid Maker',
        initialWidth: 150,
        cellClass: 'ag-right-aligned-cell',
        headerClass: 'ag-right-aligned-header',
        cellClassRules: { ...lightenCellClassRules, ...bidCellClassRules },
      },
      {
        type: 'size',
        field: 'bidQty',
        id: 'BidQty',
        title: 'Bid Qty',
        initialWidth: 90,
        params: { currencyField: 'Currency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        cellClassRules: { ...lightenCellClassRules, ...bidCellClassRules },
      },
      {
        type: 'price',
        field: 'bidDisplayPx',
        id: 'BidPx',
        title: 'Bid',
        initialWidth: 90,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingRow(data) && isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingRow(data) && isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
          expressedOutcomeField: 'ExpressedOutcome',
        } satisfies PriceParams<QuotesBlotterRow>,
        cellClassRules: { ...lightenCellClassRules, ...bidCellClassRules },
      },
      // Spread
      {
        id: 'spread',
        type: 'spread',
        title: 'Spread',
        params: {
          bidField: 'bidDisplayPx',
          askField: 'offerDisplayPx',
          pricingModeField: 'PricingMode',
        } satisfies SpreadColumnParams<QuotesBlotterRow>,
        cellClassRules: lightenCellClassRules,
      },
      // Offer
      {
        type: 'price',
        field: 'offerDisplayPx',
        title: 'Offer',
        id: 'OfferPx',
        initialWidth: 90,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingRow(data) && isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingRow(data) && isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
          align: 'left',
          expressedOutcomeField: 'ExpressedOutcome',
        } satisfies PriceParams<QuotesBlotterRow>,
        cellClassRules: { ...lightenCellClassRules, ...offerCellClassRules },
      },
      {
        type: 'size',
        field: 'offerQty',
        id: 'OfferQty',
        title: 'Offer Qty',
        initialWidth: 90,
        params: {
          currencyField: 'Currency',
          align: 'left',
          inlineFormattedNumberAlignment: 'left',
        } satisfies SizeColumnParams<QuotesBlotterRow>,
        cellClass: 'ag-left-aligned-cell',
        cellClassRules: { ...lightenCellClassRules, ...offerCellClassRules },
      },
      {
        type: 'market',
        field: 'offer.Market',
        title: 'Offer Maker',
        initialWidth: 150,
        cellClassRules: { ...lightenCellClassRules, ...offerCellClassRules },
      },
      {
        field: 'QuoteStatus',
        type: 'quoteStatus',
        title: 'Status',
        params: {
          tooltipField: 'Text',
        } satisfies QuoteStatusColumnParams<QuotesBlotterRow>,
      },

      {
        field: 'quote.subAccounts',
        type: 'subAccount',
        title: 'Sub Account(s)',
      },
      {
        field: 'User',
        type: 'user',
        title: 'User',
      },

      // Care Order enrichment columns (feature-flagged)
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.OrderID',
        type: 'id',
        initialWidth: 80,
        title: 'Care Order ID',
      },
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.initiatingFrom',
        type: 'text',
        title: 'Initiating From',
        initialWidth: 140,
      },
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.Counterparty',
        type: 'counterparty',
        title: 'Counterparty',
        initialWidth: 180,
      },
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.fund',
        type: 'text',
        title: 'Fund',
        initialHide: true,
      },
      {
        field: 'units',
        type: 'number',
        title: 'Units',
        initialWidth: 100,
        initialHide: true,
      },

      { field: 'quote.PricingMode', type: 'text', title: 'Pricing Mode', initialHide: true },
      {
        field: 'ExpressedOutcome',
        title: 'Expressed Outcome',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.ClearingAccount',
        type: 'marketAccount',
        title: 'Clearing Account',
        initialHide: true,
      },
      {
        field: 'quote.ParentRFQID',
        title: 'Parent ID',
        type: 'id',
        initialHide: true,
      },
      {
        field: 'quote.SweepGroupID',
        title: 'Sweep Group ID',
        type: 'id',
        initialHide: true,
      },
      {
        field: 'quote.QuoteID',
        title: 'Quote ID',
        type: 'id',
        initialHide: true,
      },
      {
        field: 'quote.Markets',
        type: 'quoteMarkets',
        title: 'Markets',
        initialHide: true,
      },
      {
        field: 'quote.Timestamp',
        title: 'Timestamp',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.MessageID',
        title: 'Message ID',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Currency',
        title: 'Currency',
        type: 'currency',
        initialHide: true,
      },
      {
        field: 'quote.QuoteReqID',
        title: 'Quote Req ID',
        type: 'id',
        initialHide: true,
      },
      {
        field: 'quote.QuoteRequestRejectReason',
        title: 'Quote Request Reject Reason',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.AmountCurrency',
        title: 'Amount Currency',
        type: 'currency',
        initialHide: true,
      },
      {
        field: 'quote.FirstTransactTime',
        title: 'First Transact Time',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.EndTime',
        title: 'End Time',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.SettleDate',
        title: 'Settlement Date',
        type: 'date',
        params: { customFormatter: formattedUTCDay },
        initialHide: true,
      },
      {
        field: 'quote.ExternalComments',
        title: 'External Comments',
        type: 'text',
        initialHide: true,
      },
      {
        id: 'sizeCountering',
        field: 'sizeCountering',
        title: 'Size Countering',
        type: 'text',
        description: 'Shows if Size Countering is enabled',
        initialHide: true,
      },
      {
        field: 'quote.TradedPx',
        title: 'Traded Px',
        type: 'price',
        params: { assetField: 'quote.Symbol' } satisfies PriceParams<QuotesBlotterRow>,
        initialHide: true,
      },
      {
        field: 'quote.TradedAmt',
        type: 'size',
        title: 'Traded Amt',
        params: { currencyField: 'quote.AmountCurrency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        initialHide: true,
      },
      {
        field: 'quote.TradedSide',
        title: 'Traded Side',
        type: 'side',
        initialHide: true,
      },
      { field: 'quote.tradedMarkets', type: 'markets', title: 'Traded Markets', sortable: true, initialHide: true },
      {
        field: 'quote.TransactTime',
        title: 'Transact Time',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.EventIndicator',
        title: 'Event Indicator',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Text',
        title: 'Text',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Group',
        title: 'Group',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Parameters',
        title: 'Parameters',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.fixingIndices',
        title: 'Fixing Index',
        type: 'fixingIndices',
        id: 'fixingIndices',
        initialHide: true,
      },
      {
        field: 'quote.FixingDetails',
        id: 'fixingPrice',
        title: 'Fixing Price',
        type: 'fixingPrice',
        initialHide: true,
      },
      {
        field: 'quote.fixingDate',
        id: 'fixingDate',
        title: 'Fixing Date',
        type: 'dateOnly',
        initialHide: true,
      },
    ] satisfies (ColumnDef<QuotesBlotterRow> | false)[]);

    return [...baseColumns, ...predictionColumns, actionColumn];
  }, [actionColumn, enrichRFQBlotterV2WithCareOrders, predictionColumns]);

  return columns;
};
